<template>
  <div class="dashboard">
    <h2>数据看板</h2>
    
    <!-- 顶部统计卡片 -->
    <div class="stats-grid">
      <div class="stat-card" @click="goToPointsRecharge">
        <div class="stat-icon points-icon">
          <el-icon><Coin /></el-icon>
        </div>
        <div class="stat-content">
          <h3>积分余额</h3>
          <p class="value">{{ formatNumber(dashboardData.pointsBalance) }}</p>
        </div>
      </div>
      <div class="stat-card" @click="goToMonitorResult">
        <div class="stat-icon success-icon">
          <el-icon><CircleCheck /></el-icon>
        </div>
        <div class="stat-content">
          <h3>总解析成功数</h3>
          <p class="value">{{ formatNumber(dashboardData.successCount) }}</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon today-icon">
          <el-icon><VideoPlay /></el-icon>
        </div>
        <div class="stat-content">
          <h3>本周成功数</h3>
          <p class="value">{{ formatNumber(dashboardData.weekSuccess) }}</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon month-icon">
          <el-icon><TrendCharts /></el-icon>
        </div>
        <div class="stat-content">
          <h3>本月成功数</h3>
          <p class="value">{{ formatNumber(dashboardData.monthSuccess) }}</p>
        </div>
      </div>
    </div>

    <!-- ECharts矩形图 -->
    <div class="chart-container">
      <h3>最近15天数据统计</h3>
      <div ref="chart" class="chart" style="height: 400px;"></div>
    </div>
  </div>
</template>

<script>
import { Coin, CircleCheck, VideoPlay, TrendCharts } from '@element-plus/icons-vue'
import * as echarts from 'echarts'

export default {
  name: 'DashboardView',
  components: {
    Coin,
    CircleCheck,
    VideoPlay,
    TrendCharts
  },
  data() {
    return {
      loading: false,
      dashboardData: {
        pointsBalance: 0,      // user_score
        successCount: 0,       // total_success
        weekSuccess: 0,        // week_success
        monthSuccess: 0,       // month_success
        chartData: []          // recent_15_days
      },
      chart: null
    }
  },
  mounted() {
    this.fetchDashboardData()
  },
  beforeUnmount() {
    if (this.chart) {
      this.chart.dispose()
    }
  },
  methods: {
    async fetchDashboardData() {
      this.loading = true
      try {
        // 调用API获取数据
        const response = await this.$axios.get('api/task/dashboard')
        
        // 根据用户提供的API响应结构，code=1表示成功
        if (response.code !== 1) {
          this.$message.error(response.msg || '获取数据失败')
          return
        }
        
        const data = response.data || {}
        
        // 更新数据，根据API返回的字段名映射
        this.dashboardData = {
          pointsBalance: data.user_score || 0,           // 用户积分
          successCount: data.task_statistics?.total_tasks || 0,         // 总成功数
          weekSuccess: data.week_success || 0,           // 本周成功数
          monthSuccess: data.month_success || 0,         // 本月成功数
          chartData: data.recent_15_days || []           // 最近15天数据
        }
        
        // 初始化图表
        this.initChart()
        
      } catch (error) {
        console.error('获取数据看板数据失败:', error)
        this.$message.error('获取数据失败')
        
        
        this.initChart()
      } finally {
        this.loading = false
      }
    },
    
    initChart() {
      const chartDom = this.$refs.chart
      if (!chartDom) return
      
      this.chart = echarts.init(chartDom)
      
      const chartData = this.dashboardData.chartData
      // 格式化日期，只显示月-日
      const dates = chartData.map(item => {
        const date = new Date(item.date)
        return `${date.getMonth() + 1}-${date.getDate().toString().padStart(2, '0')}`
      })
      const total = chartData.map(item => item.total || 0)
      const success = chartData.map(item => item.success || 0)
      const failed = chartData.map(item => item.failed || 0)
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            label: {
              backgroundColor: '#6a7985'
            }
          }
        },
        legend: {
          data: ['总查询数', '成功数', '失败数'],
          top: 10
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates,
          axisLabel: {
            interval: 0,
            rotate: 45
          }
        },
        yAxis: {
          type: 'value',
          name: '查询数量',
          axisLine: {
            show: true,
            lineStyle: {
              color: '#409EFF'
            }
          },
          axisLabel: {
            formatter: '{value}'
          }
        },
        series: [
          {
            name: '总查询数',
            type: 'line',
            smooth: true,
            lineStyle: {
              width: 3,
              color: '#409EFF'
            },
            itemStyle: {
              color: '#409EFF'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: 'rgba(64, 158, 255, 0.3)'
                },
                {
                  offset: 1,
                  color: 'rgba(64, 158, 255, 0.1)'
                }
              ])
            },
            data: total,
            markPoint: {
              data: [
                { type: 'max', name: '最大值' },
                { type: 'min', name: '最小值' }
              ]
            },
            markLine: {
              data: [{ type: 'average', name: '平均值' }]
            }
          },
          {
            name: '成功数',
            type: 'line',
            smooth: true,
            lineStyle: {
              width: 3,
              color: '#67C23A'
            },
            itemStyle: {
              color: '#67C23A'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: 'rgba(103, 194, 58, 0.3)'
                },
                {
                  offset: 1,
                  color: 'rgba(103, 194, 58, 0.1)'
                }
              ])
            },
            data: success,
            markPoint: {
              data: [
                { type: 'max', name: '最大值' },
                { type: 'min', name: '最小值' }
              ]
            },
            markLine: {
              data: [{ type: 'average', name: '平均值' }]
            }
          },
          {
            name: '失败数',
            type: 'line',
            smooth: true,
            lineStyle: {
              width: 3,
              color: '#F56C6C'
            },
            itemStyle: {
              color: '#F56C6C'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: 'rgba(245, 108, 108, 0.3)'
                },
                {
                  offset: 1,
                  color: 'rgba(245, 108, 108, 0.1)'
                }
              ])
            },
            data: failed,
            markPoint: {
              data: [
                { type: 'max', name: '最大值' },
                { type: 'min', name: '最小值' }
              ]
            },
            markLine: {
              data: [{ type: 'average', name: '平均值' }]
            }
          }
        ]
      }

      this.chart.setOption(option)
      
      // 响应式调整
      window.addEventListener('resize', () => {
        this.chart.resize()
      })
    },
    
    formatNumber(num) {
      if (num >= 10000) {
        return (num / 10000).toFixed(1) + '万'
      }
      return num.toLocaleString('zh-CN')
    },
    
    goToPointsRecharge() {
      this.$router.push('/points-recharge')
    },
    
    goToMonitorResult() {
      this.$router.push('/monitor-result')
    }
  }
}
</script>

<style scoped>
.dashboard {
  padding: 20px;
}

/* 面包屑样式 */
.breadcrumb-container {
  margin-bottom: 20px;
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

.stats-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin: 20px 0;
}

.stat-card {
  flex: 1;
  min-width: 200px;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  gap: 16px;
  transition: transform 0.3s, box-shadow 0.3s;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(0,0,0,0.15);
}

/* 可点击卡片的样式 */
.stat-card[onclick] {
  cursor: pointer;
}

.stat-card[onclick]:hover {
  background-color: #f8f9fa;
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.points-icon {
  background: linear-gradient(135deg, #409EFF, #66B1FF);
  color: white;
}

.success-icon {
  background: linear-gradient(135deg, #67C23A, #85CE61);
  color: white;
}

.today-icon {
  background: linear-gradient(135deg, #E6A23C, #EBB563);
  color: white;
}

.month-icon {
  background: linear-gradient(135deg, #F56C6C, #F78989);
  color: white;
}

.stat-content {
  flex: 1;
}

.stat-content h3 {
  margin: 0 0 8px 0;
  color: #606266;
  font-size: 14px;
  font-weight: normal;
}

.stat-content .value {
  font-size: 28px;
  font-weight: bold;
  margin: 0;
  color: #303133;
}

.chart-container {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-top: 24px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.chart-container h3 {
  margin: 0 0 20px 0;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
}

.chart {
  width: 100%;
}
</style>
